import { useQuery } from "@tanstack/react-query";
import { api, type LeadQuery } from "./api";

/**
 * The list a lead page was opened FROM, so ← / → step through it (§7.4, D-32).
 *
 * The problem this solves is small and constant: working a review queue meant clicking a lead, doing
 * something, pressing Back, finding your place, and clicking the next one. Twenty leads is eighty
 * interactions, and the Back step silently loses the filter and sort on a slow connection.
 *
 * **The context lives in the URL, not in a store.** `?q=<list-id>&i=<n>`. That is the whole design
 * decision, and it is what makes refresh, Back, Forward, a bookmarked link and a second tab all behave:
 * a React context would be correct until the first page reload and then quietly wrong. It also means the
 * cost is one extra query string, and the fallback when it is absent is well-defined — the Leads default
 * sort, which is where a lead opened from anywhere else sensibly belongs.
 *
 * A list id is one of:
 *
 *     today                      §7.1's review queue, in the order the page showed it
 *     leads?<search>             §7.3's table, carrying its own filter and sort verbatim
 *     archived?<search>          /archived, same
 *
 * Encoding the search rather than a snapshot of the ids keeps the list live: a lead that leaves the
 * filter between opening it and pressing → is gone from the list, which is the honest answer. A snapshot
 * would step onto a lead that no longer matches and offer no way to tell why.
 */

/** How many rows one window holds. The API caps page_size at 200; this is that cap, deliberately. */
export const WINDOW = 200;

export type ListKind = "today" | "leads" | "archived";

export interface ListContext {
  id: string;
  kind: ListKind;
  index: number;
}

/** Build the id for a list, from the search params the page is currently using. */
export function listId(kind: ListKind, search?: URLSearchParams | string): string {
  if (kind === "today") return "today";
  const raw = typeof search === "string" ? search : (search?.toString() ?? "");
  // `view`, `page` and the selection have nothing to do with the ORDER of the list, and including them
  // would make two ids that mean the same list — which shows up as the position resetting when someone
  // flips from Table to Kanban.
  const cleaned = new URLSearchParams(raw);
  for (const key of ["view", "page", "i", "q_list"]) cleaned.delete(key);
  const s = cleaned.toString();
  return s ? `${kind}?${s}` : kind;
}

/** The link to a lead that carries its place in the list (§7.4). */
export function leadHref(leadId: string, list: string | null, index: number): string {
  if (!list) return `/leads/${leadId}`;
  return `/leads/${leadId}?q=${encodeURIComponent(list)}&i=${index}`;
}

export function parseListId(id: string | null): { kind: ListKind; search: URLSearchParams } {
  const fallback = { kind: "leads" as ListKind, search: new URLSearchParams() };
  if (!id) return fallback;
  const [head, rest] = id.split("?", 2);
  if (head !== "today" && head !== "leads" && head !== "archived") return fallback;
  return { kind: head, search: new URLSearchParams(rest ?? "") };
}

/** Turn a list id's search params back into the API query that produced the list. */
export function queryFor(kind: ListKind, search: URLSearchParams, index: number): LeadQuery {
  const page = Math.floor(Math.max(index, 0) / WINDOW) + 1;
  return {
    q: search.get("q") ?? undefined,
    stage: search.getAll("stage").length ? search.getAll("stage") : undefined,
    dead_reason: search.getAll("dead_reason").length ? search.getAll("dead_reason") : undefined,
    suggestion_state: search.getAll("suggestion_state").length
      ? search.getAll("suggestion_state")
      : undefined,
    overdue: search.get("overdue") === "true" || undefined,
    stale: search.get("stale") === "true" || undefined,
    has_suggestion: search.get("has_suggestion") === "true" || undefined,
    hot: search.get("hot") === "1" || search.get("hot") === "true" || undefined,
    archived: kind === "archived" || undefined,
    sort: search.get("sort") ?? undefined,
    order: search.get("order") === "desc" ? "desc" : undefined,
    page,
    page_size: WINDOW,
  };
}

export interface LeadNav {
  /** 1-based, for "n of N". */
  position: number;
  total: number;
  prev: { id: string; index: number } | null;
  next: { id: string; index: number } | null;
  label: string;
  loading: boolean;
}

const LABELS: Record<ListKind, string> = {
  today: "Today's review queue",
  leads: "Leads",
  archived: "Archived",
};

/**
 * Where this lead sits in the list it came from, and what is on either side of it.
 *
 * The list is re-fetched rather than passed in, because the lead page is reached by URL from anywhere —
 * a link in an email, a bookmark, a refresh — and a prop would be present in exactly the one case where
 * it is not needed.
 */
export function useLeadNav(leadId: string, listIdParam: string | null, index: number): LeadNav {
  const { kind, search } = parseListId(listIdParam);
  const query = queryFor(kind, search, index);

  const leads = useQuery({
    queryKey: ["lead-nav", "list", kind, search.toString(), query.page],
    queryFn: () => api.leads(query),
    enabled: kind !== "today",
    staleTime: 30_000,
  });
  const overview = useQuery({
    queryKey: ["lead-nav", "today"],
    queryFn: () => api.overview(),
    enabled: kind === "today",
    staleTime: 30_000,
  });

  const ids =
    kind === "today"
      ? (overview.data?.pending_suggestions ?? []).map((item) => item.id)
      : (leads.data?.items ?? []).map((item) => item.id);
  const total = kind === "today" ? ids.length : (leads.data?.total ?? ids.length);
  const offset = kind === "today" ? 0 : (query.page! - 1) * WINDOW;

  // The index in the URL is a hint, not gospel: the list may have moved under it. If the lead we are
  // actually looking at is in the window, its real position wins — which is what keeps "3 of 22" honest
  // after somebody else's edit changed the sort.
  const found = ids.indexOf(leadId);
  const resolved = found >= 0 ? offset + found : index;
  const within = found >= 0 ? found : resolved - offset;

  const prevId = within > 0 ? ids[within - 1] : undefined;
  const nextId = within >= 0 && within + 1 < ids.length ? ids[within + 1] : undefined;

  return {
    position: resolved + 1,
    total,
    prev: prevId ? { id: prevId, index: resolved - 1 } : null,
    next: nextId ? { id: nextId, index: resolved + 1 } : null,
    label: LABELS[kind],
    loading: kind === "today" ? overview.isLoading : leads.isLoading,
  };
}
